import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface NotaPendente {
  filial: string;
  numero: string;
  // Numero da RPS (SDS.DS_NUMRPS, formato AAAA+numero). Referencia que os
  // usuarios usam no dia a dia - exibido na coluna 'Nº RPS' do listing.
  numeroRps?: string;
  // Numero da NF-e (SDS.DS_NFELETR), enviado pelo backend na chave
  // 'numeroNfe'. Exibido na coluna de NF-e do listing.
  numeroNfe?: string;
  serie: string;
  fornecedor: string;
  fornecedorFantasia: string;
  fornecedorEndereco: string;
  fornecedorBairro: string;
  fornecedorMunicipio: string;
  fornecedorEstado: string;
  fornecedorCep: string;
  fornecedorPais: string;
  fornecedorComplemento: string;
  // Tomador (filial Protheus corrente)
  tomadorRazaoSocial: string;
  tomadorPais: string;
  // Impostos detalhados
  impostoPIS: number;
  impostoCOFINS: number;
  impostoIR: number;
  impostoINSS: number;
  impostoCSLL: number;
  tomadorNome: string;
  tomadorFantasia: string;
  tomadorCnpj: string;
  // Privilegio SEMPROC (menu /notas-list) resolvido pelo backend por nota.
  // Habilita a opcao "Sem processo" independente da especie. A regra de CTe
  // de tomador DAAS continua sendo avaliada no proprio front, pelo CNPJ.
  // Chamado 00017826.
  podeSemProcesso?: boolean;
  tomadorEndereco: string;
  tomadorBairro: string;
  tomadorMunicipio: string;
  tomadorEstado: string;
  tomadorCep: string;
  tomadorComplemento: string;
  fornecedorCnpj: string;
  fornecedorCod: string;
  fornecedorLoja: string;
  tipo: string;            // NFe / NFSe
  status: string;          // 'Pendente'
  total: number;
  emissao: string;         // ISO yyyy-mm-dd
  recebida: string;
  origem: string;          // 'Transmite'
  oculto: string;          // 'Sim' / 'Nao'
  chaveNfe?: string;
  // Processo (DS_ZREFEXT) - referencia externa da pre-nota. Vazio quando
  // a SDS chegou sem processo. Coluna 'Processo' do listing.
  processo?: string;
  // CNAE do servico extraido do XML da NFSe (DT_PRODFOR no SDT).
  // cnae = valor cru; cnaeNorm = normalizado via U_PnfNormCnae (formato
  // que casa com a lista do /portalnf/notas/cnaes pra pre-selecao da TES).
  cnae?: string;
  cnaeNorm?: string;
  // Simples Nacional do emitente: detectado via tag <indSN>1</indSN> do XML
  // (CKO). Usado pelo executar-lote pra carregar TES de regime SIMPLES na
  // planilha REGRAS CTE.
  simplesNacional?: boolean;
  // Valor do pedagio do CTe (vPrest > comp > xNome=PEDAGIO). Backend extrai
  // do XML CKO no LIST. Frontend usa pra pre-preencher campo editavel na UI
  // de executar-lote (e mandar de volta no aprovar via F1_VALPEDG).
  valorPedagio?: number;
}

export interface AprovarNotaItem {
  filial: string;
  numero: string;
  serie: string;
  fornecedor: string;
  loja: string;
  numeroNf?: string;
  processo?: string;
  geraFinanceiro?: string;  // 'sim' | 'nao'
  vencimento?: string;      // ISO
  tipoOperacao?: string;    // codigo TES
  // CNAE do servico (1104.01 etc) - usado pelo backend pra resolver
  // F1_NATUREZ via U_PnfNatFin alem do TPO Conexos no upload de anexos.
  cnae?: string;
  // Codigo do produto Protheus (15 chars) resolvido do CNAE via
  // U_PnfPrdSrv. Vira D1_COD no SD1 - alternativa ao default da SDS.
  produto?: string;
  // Pedagio do CTe (R$). Backend grava F1_VALPEDG e deduz do D1_TOTAL.
  valorPedagio?: number;
  // Simples Nacional do emitente (CTe). Backend usa pra resolver produto
  // alternativo quando produto nao vier explicitamente.
  simplesNacional?: boolean;
  // Retencao toggle (CTe): true quando TES tem ICMS. Espelha U_PnfNatFin.
  retencao?: boolean;
  // BU resolvida do processo (W2_BU) - vai pra D1_ITEMCTA no item.
  bu?: string;
  // Flag CTe Sem Processo DAAS (regra 2026-05-27): true quando operador
  // marcou "Sem processo = Sim" na UI. Backend valida que o tomador eh
  // DAAS via U_PnfDaasTomador e, se OK, forca TES 403 + natureza 293014
  // ignorando o tipoOperacao do payload. Vide tomadorDaas na resposta
  // de detalhe.
  semProcesso?: boolean;
  // Anexos da nota, enviados JUNTO com a aprovacao (regra 2026-08). O backend
  // grava os arquivos no servidor ANTES de efetivar (staging) e so vincula
  // depois que a nota existe. Antes o anexo era um segundo request disparado
  // pelo front apos a resposta - se a resposta se perdesse (timeout do lote,
  // aba fechada, rede) a nota era efetivada e o anexo se perdia sem registro.
  anexos?: AnexoPayloadItem[];
}

// Anexo trafegado dentro do payload da aprovacao. Mesmo formato aceito pelo
// endpoint /portalnf/anexos/upload, para que os dois caminhos usem a mesma
// regra no backend (AnexosPortalNF:prepareAnexo / commitAnexo).
export interface AnexoPayloadItem {
  tipo: string;             // 'DAN' | 'CTE' | 'BOL' | 'ROM'
  filename: string;
  contentType: string;
  base64: string;
  gedFolder?: string;
  cnae?: string;
  dtVencimento?: string;    // ISO yyyy-mm-dd
}

export interface AprovarResultItem {
  numero: string;
  ok: boolean;
  doc?: string;
  erro?: string;
  // Resultado da vinculacao dos anexos, feita pelo backend no mesmo request.
  // anexosErr > 0 significa nota efetivada com anexo pendente de reenvio.
  anexosOk?: number;
  anexosErr?: number;
  anexosErros?: string;
  // Serie efetivamente usada na entrega. O backend escalona S1 -> S2 -> S3
  // (parametro MV_PNFSER) quando o numero ja existe para o fornecedor, entao
  // a chave do anexo (ZOF) tem de usar ESTA serie, e nao a que a listagem
  // exibia antes da entrega.
  serie?: string;
  // Preenchido quando houve escalonamento ("Nota entregue com a serie S2
  // (numero ja utilizado na serie S1).").
  aviso?: string;
}

interface ListResponse {
  success: boolean;
  data: NotaPendente[];
  page?: number;
  pageSize?: number;
  hasNext?: boolean;
}

export interface PageResult<T> {
  data: T[];
  hasNext: boolean;
}

export interface NotasPendentesFiltros {
  // Busca rapida (barra superior): LIKE (OR) em DS_DOC, DS_NUMRPS e DS_NFELETR.
  busca?: string;
  numero?: string;
  // Numero da RPS (SDS.DS_NUMRPS) - filtro independente do numero da nota.
  rps?: string;
  fornecedor?: string;
  // Codigo da filial Protheus (ex.: '003001001') resolvido a partir do CNPJ
  // do tomador via FilialService.codigoByCnpj. Backend filtra SDS.DS_FILIAL.
  filial?: string;
  oculto?: string;        // 'Sim' | 'Nao' | ''
  tipo?: string;          // 'NFSe' | 'CTe' | ''
  emissaoDe?: string;     // ISO yyyy-mm-dd
  emissaoAte?: string;
  totalDe?: number | string | null;
  totalAte?: number | string | null;
}

interface AprovarResponse {
  success: boolean;
  totalOk: number;
  totalErr: number;
  results: AprovarResultItem[];
}

@Injectable({ providedIn: 'root' })
export class NotasPendentesService {
  private readonly base = 'portalnf/notas-pendentes';

  constructor(private http: HttpClient) {}

  list(page = 1, pageSize = 10, filtros: NotasPendentesFiltros = {}): Observable<PageResult<NotaPendente>> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize));
    if (filtros.busca)      params = params.set('busca',      filtros.busca);
    if (filtros.numero)     params = params.set('numero',     filtros.numero);
    if (filtros.rps)        params = params.set('rps',        filtros.rps);
    if (filtros.fornecedor) params = params.set('fornecedor', filtros.fornecedor);
    if (filtros.filial)     params = params.set('filial',     filtros.filial);
    if (filtros.oculto)     params = params.set('oculto',     filtros.oculto);
    if (filtros.tipo)       params = params.set('tipo',       filtros.tipo);
    if (filtros.emissaoDe)  params = params.set('emissaoDe',  filtros.emissaoDe);
    if (filtros.emissaoAte) params = params.set('emissaoAte', filtros.emissaoAte);
    if (filtros.totalDe != null && filtros.totalDe !== '')   params = params.set('totalDe',  String(filtros.totalDe));
    if (filtros.totalAte != null && filtros.totalAte !== '') params = params.set('totalAte', String(filtros.totalAte));
    return this.http.get<ListResponse>(this.base, { params }).pipe(
      map(r => ({ data: r.data ?? [], hasNext: !!r.hasNext }))
    );
  }

  aprovar(notas: AprovarNotaItem[]): Observable<AprovarResponse> {
    return this.http.post<AprovarResponse>(`${this.base}/aprovar`, { notas });
  }
}
